import React, { useState } from 'react';
import CodeBlock from './CodeBlock';
import { useMermaid } from './useMermaid';
import { FaDownload, FaExpand } from 'react-icons/fa';
import { downloadContent } from '../../../../../../../Utils/downloadUtils/DownloadUtils';
import { useAppStore } from '../../../../../../../GlobalStore/GlobalStore.ts';
import { getFormattedEnvName } from '../../../../../../../Utils/misc/envNameUtils.ts';

interface MermaidBlockProps {
    children: string;
    index?: number;
}

const MermaidBlock: React.FC<MermaidBlockProps> = ({ children, index = 0 }) => {
    const { ref: mermaidRef, pngSrc, hasError, isRendering } = useMermaid(children);
    const environment = useAppStore((state: any) => state.environmentObj.environment);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const [isDownloading, setIsDownloading] = useState(false);
    const canDownload = getFormattedEnvName(environment) === 'Web';

    const openDiagramViewer = () => {
        if (!pngSrc) return;

        setDynamicStateObj({
            visiblePopup: "enlargedContent",
            enlargedContent: {
                type: "image",
                content: pngSrc,
                alt: `Mermaid diagram ${index + 1}`,
                filename: `mermaid-diagram-${index + 1}.png`
            },
        });
    };

    const handleDownload = async () => {
        if (!pngSrc || isDownloading) return;

        try {
            setIsDownloading(true);
            await downloadContent({
                content: pngSrc,
                filename: `mermaid-diagram-${index + 1}.png`,
                type: 'png'
            });
        } catch (error) {
            console.error('Failed to download Mermaid diagram:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadPress = (event: React.MouseEvent | React.TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
        void handleDownload();
    };

    if (hasError) {
        return (
            <CodeBlock
                language="mermaid"
                index={index}
            >
                {children}
            </CodeBlock>
        );
    }

    return (
        <div className="rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200">
            <div
                ref={mermaidRef}
                className="mermaid pointer-events-none fixed left-[-99999px] top-0 opacity-0"
                aria-hidden="true"
            />
            {pngSrc ? (
                <div className="flex w-full flex-col items-start gap-3">
                    <div className="w-full max-w-[56%] sm:max-w-[48%]">
                        <button
                            className="w-full cursor-zoom-in"
                            onClick={openDiagramViewer}
                            type="button"
                        >
                            <img
                                src={pngSrc}
                                alt={`Mermaid diagram ${index + 1}`}
                                className="mx-auto block h-auto w-full rounded-xl"
                            />
                        </button>
                        <div className="mt-3 flex flex-wrap items-center justify-start gap-2">
                            <button
                                onClick={openDiagramViewer}
                                className="pointer-events-auto touch-manipulation rounded-md border border-neutral-200/80 bg-white/90 p-2 text-neutral-700 shadow-sm transition-all active:scale-[0.98] hover:bg-white dark:border-neutral-700 dark:bg-neutral-900/85 dark:text-neutral-200 dark:hover:bg-neutral-800"
                                title="Open diagram"
                                aria-label="Open diagram"
                                type="button"
                            >
                                <FaExpand className="h-3.5 w-3.5" />
                            </button>
                            {canDownload && (
                                <button
                                    onClick={handleDownloadPress}
                                    onTouchEnd={handleDownloadPress}
                                    disabled={isDownloading}
                                    className={`pointer-events-auto touch-manipulation rounded-md border border-neutral-200/80 bg-white/90 p-2 text-neutral-700 shadow-sm transition-all active:scale-[0.98] dark:border-neutral-700 dark:bg-neutral-900/85 dark:text-neutral-200 ${isDownloading ? 'cursor-not-allowed opacity-60' : 'hover:bg-white dark:hover:bg-neutral-800'}`}
                                    title={isDownloading ? 'Downloading...' : 'Download PNG'}
                                    aria-label={isDownloading ? 'Downloading diagram' : 'Download diagram'}
                                    type="button"
                                >
                                    <FaDownload className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="min-h-[120px] w-full animate-pulse rounded-md bg-neutral-100 dark:bg-neutral-900/60" aria-hidden={isRendering} />
            )}
        </div>
    );
};

export default MermaidBlock;
